import Vue from 'vue'
import Vuex, {Store} from 'vuex'
import { CognitoUserPool, CookieStorage, CognitoUser } from 'amazon-cognito-identity-js'
import decode from 'jwt-decode'
import { tenantRoles, isUserProfileIncomplete } from '../utils/PfxUtils.js'

const appConfig = process.env.config
const COGNITO_CLIENT_ID = appConfig.cognito.ClientId
const COGNITO_LOGOUT_API = appConfig.cognito.logoutApi
const COGNITO_LOGOUT_CALLBACK = appConfig.cognito.logoutCallback
const cookieDomain = appConfig.frontend.cookieDomain
const cognitoStorage = cookieDomain ? new CookieStorage({ domain: cookieDomain, path: '/', expires: 28, secure: false }) : null
const userPool = new CognitoUserPool({
  UserPoolId: appConfig.cognito.UserPoolId,
  ClientId: COGNITO_CLIENT_ID,
  Storage: cognitoStorage
})
const SESSION_STORAGE_USER = 'SESSION_STORAGE_USER'

Vue.use(Vuex)

export default new Store({

  state: {
    bidsDialog: 0, // value is the auction id to be shown
    agentCardDialog: {
      visible: false,
      agentId: '',
      agentProfile: {},
      tenantSubdomain: ''
    },
    autobid: {
      allowed: false,
      dialog: false
    },
    dialog: {
      visible: null,
      fullscreen: false,
      width: 300,
      title: null,
      text: null,
      buttons: []
    },
    loginDialog: {
      visible: null,
      view: null
    },
    getStartedDialog: {
      visible: null
    },
    youTubeDialog: {
      visible: null,
      videoId: ''
    },
    headerMenuVisible: false,
    loadingSpinner: false, // full screen app-blocking spinner
    tenant: {
      subdomain: null, // special case: #pfx
      status: null,
      data: {}
    },
    authenticatedUser: null, // this must be null until initial authentication is resolved (sets false/true)
    user: {
      idToken: null,
      federated: null,
      email: null,
      cognitoSubject: null,
      profile: {
        firstName: null,
        photoUrl: null
      },
      role: null,
      tenant: null,
      pfxAdmin: false
    },
    theme: {
      brandColor: '#00b9ef',
      showBannerOverlay: true
    },
    loginView: 'login',
    headerAndFooterVisible: true,
    importData: {
      buildOut: {
        cached: false,
        data: []
      },
      rets: {
        cached: false,
        data: []
      }
    },
    newAuctionStorage: {
      edit: false,
      currentStep: 0,
      auction: {
        // name, address, zip, startDate, endDate, startingPrice, ...
      },
      auctionMetadata: {
        // apn, buildingSize, description, lat, lng, photos, ...
      },
      sellerProfile: {
        // contains seller profile data (if any)
      },
      buyerRequirements: [
        // contains strings
      ],
      files: []
    },
    targetPage: null, // from where the user left for signup
    watchlist: [],
    currentTutorialPageName: undefined
  },

  getters: {
    getFilesForNewAuction(state) {
      return state.newAuctionStorage.files
    },
    idToken(state) {
      return state.user.idToken
    },
    subdomain(state) {
      return state.tenant.subdomain
    },
    tenantActive(state) {
      return state.tenant.status === 'ACTIVE'
    },
    userId(state) {
      return state.user.cognitoSubject
    },
    isAuctionEdit(state) {
      return state.newAuctionStorage.edit === true
    },
    authenticatedUser(state) {
      return state.authenticatedUser
    },
    isPfxAdmin(state) {
      return state.user.pfxAdmin
    },
    autoBidAllowed(state) {
      return state.autobid.allowed
    },
    autoBidDialog(state) {
      return state.autobid.allowed && state.autobid.dialog
    },
    bidsDialog(state) {
      return state.bidsDialog
    },
    agentCardDialog(state) {
      return state.agentCardDialog
    },
    userPool() {
      return userPool
    },
    cognitoStorage() {
      return cognitoStorage
    },
    lat(state) {
      return Number(state.newAuctionStorage.auctionMetadata.lat) || 34.142509
    },
    lng(state) {
      return Number(state.newAuctionStorage.auctionMetadata.lng) || -118.255074
    },
    role(state) {
      return state.user.role
    },
    tenant(state) {
      return state.user.tenant
    },
    tenantRole(state) {
      if ( state.user.tenant === state.tenant.subdomain ) {
        return state.user.role
      }
      return tenantRoles.ANONYMOUS.value
    },
    fullName(state) {
      return state.user.profile ? `${state.user.profile.firstName||''} ${state.user.profile.lastName||''}`.trim() : ''
    },
    photoUrl(state) {
      return state.user.profile ? state.user.profile.photoUrl : null
    },
    cognitoSubject(state) {
      return state.user.cognitoSubject
    },
    getTargetPage(state) {
      return state.targetPage
    },
    getWatchlist(state) {
      return state.watchlist
    },
    getCurrentPageTutorialName(state) {
      return state.currentTutorialPageName
    }
  },

  mutations: {
    setCurrentTutorialPageName(state, payload){
      state.currentTutorialPageName = payload
    },
    setLoadingSpinner(state, payload) {
      if (payload === '--') {
        state.loadingSpinner--
      } else if (payload === '++') {
        state.loadingSpinner++
      } else {
        state.loadingSpinner = payload
      }
    },

    openDialog(state, payload) {
      if (typeof payload === 'string') {
        payload = { text: payload }
      }
      let buttons = payload.buttons
      if (!buttons || buttons.length === 0) {
        buttons = [ { 'title': 'OK' } ]
      }

      state.dialog.title = payload.title
      state.dialog.text = payload.text
      state.dialog.html = payload.html
      state.dialog.staticContent = payload.staticContent
      state.dialog.buttons = buttons
      state.dialog.fullscreen = payload.fullscreen || false
      state.dialog.xCloseIcon = payload.xCloseIcon || false
      state.dialog.width = payload.width || 300
      state.dialog.visible = new Date().getTime()
    },
    closeDialog(state) {
      state.dialog.visible = null
    },

    openLoginDialog(state) {
      state.loginDialog.view = 'login'
      state.loginDialog.visible = new Date().getTime()
    },
    openSignUpDialog(state) {
      state.loginDialog.view = 'signup'
      state.loginDialog.visible = new Date().getTime()
    },
    closeLoginDialog(state) {
      state.loginDialog.visible = null
      state.loginDialog.view = null
    },
    openGetStartedDialog(state) {
      state.getStartedDialog.visible = new Date().getTime()
    },
    closeGetStartedDialog(state) {
      state.getStartedDialog.visible = null
    },
    openYouTubeDialog(state, payload) {
      state.youTubeDialog.videoId = payload
      state.youTubeDialog.visible = new Date().getTime()
    },
    closeYouTubeDialog(state) {
      state.youTubeDialog.visible = null
    },

    hideHeaderAndFooter(state) {
      state.headerAndFooterVisible = false
    },

    setBrandColor(state, payload) {
      state.theme.brandColor = payload || '#00b9ef'
    },

    setShowBannerOverlay(state, payload) {
      state.theme.showBannerOverlay = payload || true
    },

    setTenant(state, payload) {
      state.tenant = payload
    },
    setTenantStatus(state, payload) {
      state.tenant.status = payload
    },

    setUser(state, payload) {
      state.authenticatedUser = payload.authenticatedUser
      state.user = payload.user || {}

      if (state.authenticatedUser) {
        sessionStorage.setItem(SESSION_STORAGE_USER, JSON.stringify(state.user))
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_USER)
        this.commit('clearNewAuctionStorage')
      }

      console.log('setUser [%s][%s]', state.authenticatedUser, state.user && state.user.cognitoSubject)
      this.dispatch('closeLoginDialogAfterAuthorize', payload)
    },

    setUserProfile(state, payload) {
      state.user.profile = payload || {}
    },

    changeLoginView(state, str) {
      state.loginView = str
    },

    setCreateListingProgressCurrentStep(state, payload) {
      console.log('setCreateListingProgressCurrentStep', payload)
      state.newAuctionStorage.currentStep = payload
    },

    setBuildOutData(state, payload) {
      console.log('store: setBuildOutData', payload)
      state.importData.buildOut = payload
    },

    setRetsData(state, payload) {
      console.log('store: setRetsData', payload)
      state.importData.rets = payload
    },

    setDataToNewAuctionStorage(state, payload) {
      console.log('store: setDataToNewAuctionStorage', payload)
      Object.assign(state.newAuctionStorage, payload)
    },

    addFileForNewAuction(state, payload) {
      return state.newAuctionStorage.files.push(payload)
    },

    clearNewAuctionStorage(state) {
      console.log('store: clearNewAuctionStorage')
      state.newAuctionStorage.edit = false
      state.newAuctionStorage.currentStep = 0
      state.newAuctionStorage.source = null
      state.newAuctionStorage.property = {}
      state.newAuctionStorage.auction = {}
      state.newAuctionStorage.auctionMetadata = { lat: 34.142509, lng: -118.255074 }
      state.newAuctionStorage.sellerProfile = {}
      state.newAuctionStorage.buyerRequirements = []
      state.newAuctionStorage.files = []
    },

    setAllowAutoBid(state, payload) {
      state.autobid.allowed = payload
    },
    showAutoBidDialog(state, payload) {
      state.autobid.dialog = payload
    },

    openBidsDialog(state, payload) {
      state.bidsDialog = payload
    },
    closeBidsDialog(state) {
      state.bidsDialog = 0
    },

    openAgentCardDialog(state, payload) {
      state.agentCardDialog = payload
      state.agentCardDialog.visible = true
    },
    closeAgentCardDialog(state) {
      state.agentCardDialog = {
        visible: false,
        agentId: state.agentCardDialog.agentId,
        agentProfile: state.agentCardDialog.agentProfile,
        tenantSubdomain: state.agentCardDialog.tenantSubdomain
      }
    },

    auctionCoordinates(state, payload) {
      Object.assign(state.newAuctionStorage.auctionMetadata, payload)
    },

    updateTenantRole(state, payload) {
      state.user.role = payload.role
      state.user.tenant = payload.tenant
    },

    setTargetPage(state, payload) {
      state.targetPage = payload
    },

    setWatchlist(state, payload) {
      state.watchlist = payload
    },

    toggleWatchlistItem(state, payload) {
      const index = state.watchlist.indexOf(payload)
      if ( index === -1 ) {
        state.watchlist.push(payload)
      } else {
        state.watchlist.splice(index, 1)
      }
    }
  },

  actions: {
    authorize(context, payload) {
      context.commit('setLoadingSpinner', true)
      const closeLoginDialogAfter = payload.closeLoginDialogAfter || false
      const noActionsAfterLogin = payload.noActionsAfterLogin || false
      const doTenantRedirect = payload.doTenantRedirect || false
      const user = userPool.getCurrentUser()
      const userInStorage = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_USER)) // cannot use userPool with social login > own additional sessionStorage implementation
      if (user) {
        user.getSession((err, session) => {
          if (err) {
            console.error('user.getSession failed', err)
            context.commit('setUser', { authenticatedUser: false, user: {} })
            context.commit('setLoadingSpinner', false)
            return
          }
          const idToken = session.getIdToken().jwtToken
          this.dispatch('authorizeWithIdToken', { idToken, federated: false, router: payload.router, closeLoginDialogAfter, noActionsAfterLogin, doTenantRedirect })
        })
      } else if (userInStorage && userInStorage.idToken) {
        this.dispatch('authorizeWithIdToken', { idToken: userInStorage.idToken, federated: userInStorage.federated, router: payload.router, closeLoginDialogAfter, noActionsAfterLogin, doTenantRedirect })
      } else {
        context.commit('setUser', { authenticatedUser: false, user: {} })
        context.commit('setLoadingSpinner', false)
      }
    },

    authorizeFromFederated(context, payload) {
      const idToken = payload.idToken || ''
      const accessToken = payload.accessToken || ''
      const federated = payload.federated || false
      const closeLoginDialogAfter = payload.closeLoginDialogAfter || true
      const noActionsAfterLogin = payload.noActionsAfterLogin || false

      const decodedIdToken = idToken ? decode(idToken) : {}
      const userData = {
        Username: decodedIdToken['cognito:username'],
        Pool: userPool,
        Storage: cognitoStorage
      }
      const cognitoUser = new CognitoUser(userData)
      cognitoUser.signInUserSession = cognitoUser.getCognitoUserSession({
        AccessToken: accessToken,
        IdToken: idToken
      })
      cognitoUser.cacheTokens()

      //const user = userPool.getCurrentUser()
      //user.setSignInUserSession(cognitoUser.signInUserSession)

      this.dispatch('authorizeWithIdToken', { idToken, federated, router: payload.router, closeLoginDialogAfter, noActionsAfterLogin })
    },

    authorizeWithIdToken(context, payload) {
      context.commit('setLoadingSpinner', true)
      const closeLoginDialogAfter = payload.closeLoginDialogAfter || false
      const noActionsAfterLogin = payload.noActionsAfterLogin || false
      const doTenantRedirect = payload.doTenantRedirect || false
      const idToken = payload.idToken
      //console.debug('idToken', idToken)
      const decoded = idToken ? decode(idToken) : {}
      //console.debug('decoded idToken', decoded)
      const cognitoSubject = decoded.sub || ''
      const email = decoded.email || ''

      if (idToken) {
        Vue.PfxBackendApi.postLogin().then(res => {
          const loginResponse = res.data || {}
          const tenantRole = loginResponse.tenantRole || {}
          const pfxAdmin = loginResponse.pfxAdmin || false

          const newUser = {
            idToken,
            federated: payload.federated || false,
            cognitoSubject,
            email,
            role: tenantRole.role,
            tenant: tenantRole.tenant.subdomain,
            pfxAdmin
          }

          // load also user profile from user service (for name, photo, etc.)
          Vue.PfxBackendApi.getUser(cognitoSubject).then(res => {
            newUser.profile = res.data || {}
            context.commit('setUser', { authenticatedUser: true, user: newUser, closeLoginDialogAfter })
            context.commit('setLoadingSpinner', false)

            if (!noActionsAfterLogin && isUserProfileIncomplete(newUser.profile)) {
              payload.router.push('/profile')
            } else if (!noActionsAfterLogin && doTenantRedirect && (tenantRole.tenant.subdomain !== null && tenantRole.tenant.subdomain !== 'null')) {
              const subdomain = tenantRole.tenant.subdomain
              const pfxLandingPage = appConfig.frontend.pfxLandingPage
              const tenantLandingPage = pfxLandingPage.replace(/\/\//, '//' + subdomain + '.')
              console.log('redirect to', tenantLandingPage)
              window.location = `${tenantLandingPage}dashboard`
            } else {
              this.dispatch('loadWatchlist')
            }
          }).catch(err => {
            if (err.status === 404) {
              Vue.PfxBackendApi.createUser(idToken, decoded).then(res => {
                console.log('createUser res', res)
                newUser.profile = res.data || {}
                context.commit('setUser', { authenticatedUser: true, user: newUser, closeLoginDialogAfter })
                context.commit('setLoadingSpinner', false)

                if (!noActionsAfterLogin && !payload.router.history.current.path.startsWith('/join')) {
                  payload.router.push('/profile')
                }
              }).catch(err => {
                console.error('error creating user profile', err)
                context.commit('setUser', { authenticatedUser: true, user: newUser, closeLoginDialogAfter })
                context.commit('setLoadingSpinner', false)
              })
            } else {
              console.error('error loading user profile', err)
              context.commit('setUser', { authenticatedUser: true, user: newUser, closeLoginDialogAfter })
              context.commit('setLoadingSpinner', false)
            }
          })

        }).catch(err => {
          console.error('postLogin failed', err)
          if (err.status === 401) {
            console.error('idToken probably expired')
          }
          context.commit('setUser', { authenticatedUser: false, user: {}, closeLoginDialogAfter })
          context.commit('setLoadingSpinner', false)
        })
      } else {
        context.commit('setUser', { authenticatedUser: false, user: {} })
        context.commit('setLoadingSpinner', false)
      }
    },

    closeLoginDialogAfterAuthorize(context, payload) {
      const closeLoginDialogAfter = payload.closeLoginDialogAfter || false
      if (closeLoginDialogAfter) {
        context.commit('closeLoginDialog')
        context.commit('setLoadingSpinner', false)
      }
    },

    logout(context) {
      const federated = this.state.user && this.state.user.federated || false
      if (federated === true) {
        const url = COGNITO_LOGOUT_API +
          '?logout_uri=' + COGNITO_LOGOUT_CALLBACK +
          '&client_id=' + COGNITO_CLIENT_ID
        window.open(url, 'pfxFederatedLogoutPopup', 'width=640,height=640,menubar=no,toolbar=no,location=no,status=no')
      }

      const user = userPool.getCurrentUser()
      user && user.signOut()

      context.commit('setUser', { authenticatedUser: false, user: {} })
      context.commit('setWatchlist', [])
    },

    openTermsAndConditionsPopup(context) {
      context.commit('openDialog', {
        staticContent: 'terms',
        width: 720,
        xCloseIcon: true
      })
    },

    openBrokerRegistrationPopup(context) {
      context.commit('openDialog', {
        staticContent: 'broker',
        width: 720,
        xCloseIcon: true
      })
    },

    updateAuctionCoordinates(context, payload) {
      context.commit('auctionCoordinates', payload)
    },

    openPrivacyPolicyPopup(context) {
      context.commit('openDialog', {
        staticContent: 'privacy',
        width: 720,
        xCloseIcon: true
      })
    },

    reloadTenantRole(context) {
      console.log('Tenant role change detected. Reloading role information')
      Vue.PfxBackendApi.postLogin()
      .then(res => {
        context.commit('updateTenantRole', {
          role: res.data.tenantRole.role,
          tenant: res.data.tenantRole.tenant.subdomain
        })
      })
      .catch(err => {
        console.error('Couldn\'t refresh tenant role', err)
      })
    },

    loadWatchlist(context) {
      Vue.PfxBackendApi.loadWatchlist()
      .then(res => {
        context.commit('setWatchlist', res.data)
        console.log('loadWatchlist')
      })
      .catch(err => {
        console.error('Couldn\'t load watchlist', err)
      })
    },

    toggleWatchlistStatus({commit, state}, payload) {
      const watched = state.watchlist.indexOf(payload) !== -1
      if (state.authenticatedUser) {
        commit('toggleWatchlistItem', payload)
      }
      Vue.PfxBackendApi.toggleWatchlistStatus(payload, watched)
      .then(res => {
        commit('setWatchlist', res.data)
      })
      .catch(err => {
        console.error('Couldn\'t change watchlist status', err)
      })
    }
  }
})



// WEBPACK FOOTER //
// ./src/store/index.js